import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api, type Me } from './lib/api';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Status from './pages/Status';
import Audit from './pages/Audit';
import Qa from './pages/Qa';
import Features from './pages/Features';
import Support from './pages/Support';
import Notifications from './pages/Notifications';
import Reports from './pages/Reports';
import Spokes from './pages/Spokes';
import Landing from './pages/Landing';
import Configuration from './pages/Configuration';
import Login from './pages/Login';
import TenantsAdmin from './pages/TenantsAdmin';
import License from './pages/License';
import CoreModule from './pages/CoreModules';
import Troubleshooting from './pages/Troubleshooting';
import AdminAuthCallback from './pages/AdminAuthCallback';
import Migration from './pages/Migration';
import MigrationTracker from './pages/MigrationTracker';
import MigrationTrackerSheet from './pages/MigrationTrackerSheet';
import UccxConvert from './pages/UccxConvert';
import KnowledgeBase from './pages/KnowledgeBase';
import EmailReporting from './pages/EmailReporting';
import CdrSearch from './pages/CdrSearch';

type AuthState = { loading: boolean; me: Me | null };

function RequireAuthRedirect() {
  // Preserve the user's intended destination so the post-login redirect lands them there.
  const loc = useLocation();
  const returnTo = encodeURIComponent(loc.pathname + loc.search);
  return <Navigate to={`/login?return_to=${returnTo}`} replace />;
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ loading: true, me: null });

  useEffect(() => {
    let cancel = false;
    api.me()
      .then((me) => { if (!cancel) setAuth({ loading: false, me }); })
      .catch(() => { if (!cancel) setAuth({ loading: false, me: null }); });
    return () => { cancel = true; };
  }, []);

  if (auth.loading) {
    return (
      <div className="h-screen grid place-items-center text-gray-500">
        <div className="animate-pulse">Loading TriNetHub…</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public — only the login form is reachable signed-out. The status page
          now lives inside the authenticated app (login-gated, not public). */}
      <Route path="/login" element={auth.me ? <Navigate to="/" replace /> : <Login />} />

      {auth.me ? (
        // Signed-in: Layout wraps every authed route, including "/" → Dashboard.
        <Route element={<Layout me={auth.me} />}>
          <Route path="/" element={<Dashboard me={auth.me} />} />
          <Route path="/status" element={<Status />} />
          {/* /tenants is a legacy stub (milestone placeholder). The real tenant
              management UI is /admin/tenants — redirect there so the route isn't a dead page. */}
          <Route path="/tenants" element={<Navigate to="/admin/tenants" replace />} />
          <Route path="/audit" element={<Audit me={auth.me} />} />
          <Route path="/qa" element={<Qa me={auth.me} />} />
          <Route path="/features" element={<Features me={auth.me} />} />
          <Route path="/support" element={<Support me={auth.me} />} />
          <Route path="/notifications" element={<Notifications me={auth.me} />} />
          <Route path="/reports" element={<Reports me={auth.me} />} />
          <Route path="/spokes" element={<Spokes me={auth.me} />} />
          <Route path="/configuration" element={<Configuration me={auth.me} />} />
          {/* Webex Control Hub OAuth redirect lands here — matches the
              Redirect URI registered with the Webex Integration. Handled
              client-side; calls /api/wxcc/admin-auth/finalize. */}
          <Route path="/api/admin-auth/callback" element={<AdminAuthCallback me={auth.me} />} />
          {/* Cross-system Operations tool — live health scan + Cowork handoff */}
          <Route path="/troubleshooting" element={<Troubleshooting me={auth.me} />} />
          <Route path="/knowledge-base" element={<KnowledgeBase me={auth.me} />} />
          <Route path="/email-reporting" element={<EmailReporting me={auth.me} />} />
          <Route path="/call-detail" element={<CdrSearch />} />
          {/* M8.5: tenant CRUD + license/billing pages */}
          <Route path="/admin/tenants" element={<TenantsAdmin me={auth.me} />} />
          <Route path="/admin/license" element={<License me={auth.me} />} />
          {/* M12: Cisco/Webex core module dashboards */}
          <Route path="/modules/wxc"   element={<CoreModule moduleKey="wxc"  me={auth.me} />} />
          <Route path="/modules/wxcc"  element={<CoreModule moduleKey="wxcc" me={auth.me} />} />
          <Route path="/modules/cucm"  element={<CoreModule moduleKey="cucm" me={auth.me} />} />
          <Route path="/modules/uccx"  element={<CoreModule moduleKey="uccx" me={auth.me} />} />
          {/* CUBE/LGW split: two separate modules, each wired to its own data
              plane. Local Gateway = Webex Calling cloud API (wxc-svc); CUBE =
              on-prem device via the cube-svc spoke (lgw-svc / spoke-svc). */}
          <Route path="/modules/lgw"   element={<CoreModule moduleKey="lgw"  me={auth.me} />} />
          <Route path="/modules/cube"  element={<CoreModule moduleKey="cube" me={auth.me} />} />
          {/* SIP Tools — super SIP testing/tracing (signaling 1xx-6xx, media + two-way, Upload SIP Logs) */}
          <Route path="/modules/sip"   element={<CoreModule moduleKey="sip"  me={auth.me} />} />
          <Route path="/modules/security" element={<CoreModule moduleKey="security" me={auth.me} />} />
          {/* Migration suite — TSB/Utility Services UCCX→WxCC cutover + .aef convertor */}
          <Route path="/migration"    element={<Migration me={auth.me} />} />
          <Route path="/migration-tracker" element={<MigrationTracker me={auth.me} />} />
          <Route path="/migration-sheet" element={<MigrationTrackerSheet me={auth.me} />} />
          <Route path="/uccx-convert" element={<UccxConvert me={auth.me} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      ) : (
        <>
          {/* Signed-out: public landing at /, redirect every other authed route through /login. */}
          <Route path="/" element={<Landing />} />
          <Route path="*" element={<RequireAuthRedirect />} />
        </>
      )}
    </Routes>
  );
}
